// Chapter 13 - Schedules of Reinforcement
// Source: Cooper, Heron, & Heward (3rd ed.), Chapter 13.
// All content traced to source-pdfs/Chapter-13-Schedules-of-Reinforcement-2.pdf

import type { ChapterModule } from "./types";

const chapter13Module: ChapterModule = {
  meta: {
    id: "13",
    number: 13,
    title: "Schedules of Reinforcement",
    subtitle: "FR, VR, FI, VI; compound and concurrent schedules",
    part: "Part 4 - Reinforcement",
    estMinutes: 80,
    bacb: ["B-5", "G-15"],
  },
  curated: true,
  overview: [
    "Chapter 13 introduces SCHEDULES OF REINFORCEMENT: rules that determine the probability a specific response will produce reinforcement. Continuous reinforcement (CRF) and extinction are the boundaries; intermittent schedules sit between them. Ratio schedules require a number of responses; interval schedules require time-plus-a-response.",
    "You will learn the four basic intermittent schedules (FR, VR, FI, VI) and their characteristic response patterns (post-reinforcement pause for FR/FI, steady high rates for VR, scallop for FI, low-moderate uniform rate for VI), schedule thinning procedures, ratio strain, and compound schedules including multiple, concurrent, chained, mixed, tandem, alternative, and conjunctive schedules. Behavioral contrast, limited holds, lag schedules, DRH/DRL, and progressive-ratio schedules are also covered.",
  ],
  objectives: [
    { id: "B-5",  text: "Define and provide examples of schedules of reinforcement." },
    { id: "G-15", text: "Use reinforcement schedules to maintain behavior and to thin reinforcement over time." },
  ],
  coreConcepts: [
    {
      title: "Continuous, intermittent, and the boundaries",
      body: [
        "A schedule of reinforcement is a rule that establishes the probability that a specific occurrence of a behavior will produce reinforcement. Continuous reinforcement (CRF, every response reinforced) and extinction (no response reinforced) are the boundaries. Intermittent schedules (INT) reinforce only some occurrences. CRF is used during ACQUISITION; INT is used to MAINTAIN established behavior.",
      ],
    },
    {
      title: "Ratio vs interval; fixed vs variable",
      body: [
        "Ratio schedules require a number of responses before one produces reinforcement. Interval schedules require an elapse of time PLUS a response. Fixed schedules use a constant requirement; variable schedules use a varying requirement around an average.",
      ],
    },
    {
      title: "Four basic intermittent schedules and their patterns",
      body: [
        "FR (fixed ratio): n responses produce reinforcement. Pattern: rapid completion of the n responses, followed by a postreinforcement pause. VR (variable ratio): average of n responses; steady, high rates with no PRP. FI (fixed interval): first response after t seconds; pattern is a postreinforcement pause then an accelerating rate (the FI scallop) toward the end of the interval. VI (variable interval): average of t seconds; low-to-moderate STEADY rate, uniform slope on a cumulative graph.",
      ],
    },
    {
      title: "Postreinforcement pause and FI scallop",
      body: [
        "Both FR and FI produce a postreinforcement pause (PRP) - the duration influenced by the size of the ratio and the magnitude of reinforcement. The FI scallop is the gradually accelerating rate of response toward the end of the FI interval, reaching maximum just before reinforcer delivery.",
      ],
    },
    {
      title: "Limited hold",
      body: [
        "A limited hold (LH) added to an interval schedule means reinforcement remains available for a finite time after the FI or VI interval elapses. After the LH expires without a response, reinforcement is lost. LHs tighten the interval schedule and can shape higher rates.",
      ],
    },
    {
      title: "Thinning intermittent reinforcement",
      body: [
        "Two common procedures: (a) gradually increase the response requirement on a ratio schedule or the interval duration on an interval schedule, and (b) use instructions, rules, directions, or signs to clearly communicate the schedule. Use SMALL increments of schedule changes to avoid loss of previous gains. Ratio strain results from abrupt jumps from dense to thin schedules.",
      ],
    },
    {
      title: "DRH, DRL, DRD, and lag schedules",
      body: [
        "Differential reinforcement of high rates (DRH): reinforcement contingent on responses above a criterion rate; produces high rates. Differential reinforcement of low rates (DRL): reinforcement contingent on responses below a criterion rate; produces low rates. Differential reinforcement of diminishing rates (DRD): reinforcement at the end of a time interval when the number of responses is less than a gradually decreasing criterion. Lag schedules: reinforcement contingent on a response differing in some predetermined way from one or more prior responses (used to build variability).",
      ],
    },
    {
      title: "Progressive ratio (PR) schedule",
      body: [
        "An FR variant in which the ratio requirement INCREASES incrementally within a session. The point at which responding ceases is the BREAK POINT; PR schedules quantify reinforcer effectiveness.",
      ],
    },
    {
      title: "Compound schedules",
      body: [
        "Basic schedules can be combined to form compound schedules: SUCCESSIVE or SIMULTANEOUS; WITH or WITHOUT discriminative stimuli; with response requirements met INDEPENDENTLY or in combination.",
      ],
      bullets: [
        "Multiple (mult): basic schedules alternate in random order, each correlated with a distinct SD.",
        "Mixed (mix): like multiple, but NO SDs signal which schedule is in effect.",
        "Chained (chain): basic schedules run in sequence; an SD precedes each link.",
        "Tandem (tand): like chained, but NO SDs across links.",
        "Concurrent (conc): two or more contingencies operate independently and simultaneously for two or more behaviors, each with its own SD.",
        "Alternative (alt): two or more schedules are simultaneously available; whichever schedule's requirement is met FIRST delivers reinforcement.",
        "Conjunctive (conj): reinforcement requires the response requirements of TWO OR MORE schedules to be met simultaneously.",
      ],
    },
    {
      title: "Behavioral contrast",
      body: [
        "In a multiple schedule, a change in reinforcement rate in ONE component (e.g., extinction in one) often produces a CHANGE IN THE OPPOSITE DIRECTION in the unaltered component. This is behavioral contrast - a phenomenon to anticipate when modifying multi-component programs.",
      ],
    },
    {
      title: "Applied caveats",
      body: [
        "Effects established in basic research should be extrapolated to applied settings with caution. Applied behavior analysts should include analysis of basic intermittent schedules and compound schedules in research agendas. Adjunctive behaviors (e.g., doodling, idle talking, smoking) that develop alongside scheduled reinforcement are a relatively understudied area.",
      ],
    },
  ],
  glossary: [
    { term: "Schedule of reinforcement", definition: "A rule that establishes the probability that a specific occurrence of a behavior will produce reinforcement.", sourceHint: "A schedule of reinforcement is a rule that establishes the probability that a specific occurrence of a behavior will produce reinforcement." },
    { term: "Continuous reinforcement (CRF)", definition: "Reinforcement of every occurrence of the target behavior; used during acquisition.", sourceHint: "Continuous reinforcement (CRF) is used to strengthen behavior, primarily during the initial stages of learning new behaviors." },
    { term: "Intermittent reinforcement (INT)", definition: "Reinforcement of only selected occurrences of behavior; used to maintain established behavior.", sourceHint: "Intermittent reinforcement (INT) is used to maintain an established behavior, especially during maintenance stages of learning." },
    { term: "Fixed ratio (FR) schedule", definition: "A schedule that requires a specified, fixed number of responses before one response produces reinforcement.", sourceHint: "A fixed ratio (FR) schedule requires the completion of a specified number of responses before a response produces reinforcement." },
    { term: "Variable ratio (VR) schedule", definition: "A ratio schedule with a varying number of required responses around an average.", sourceHint: "A variable ratio (VR) schedule of reinforcement requires the completion of a variable number of responses before a response produces reinforcement." },
    { term: "Fixed interval (FI) schedule", definition: "A schedule that reinforces the first response after a constant period of time since the last reinforced response.", sourceHint: "A fixed interval (FI) schedule provides reinforcement for the first response following the elapse of a specific, constant duration of time since the last reinforced response." },
    { term: "Variable interval (VI) schedule", definition: "A schedule that reinforces the first response after a variable duration of time around an average.", sourceHint: "A variable interval (VI) schedule provides reinforcement for the first response following the elapse of a variable duration of time." },
    { term: "Postreinforcement pause", definition: "A pause in responding immediately after reinforcement on FR and FI schedules.", sourceHint: "a postreinforcement pause follows reinforcement." },
    { term: "FI scallop", definition: "The gradually accelerating rate of response toward the end of the FI interval, named for the rounded curves on a cumulative graph.", sourceHint: "This gradually accelerating rate of response toward the end of the interval is called an FI scallop because of the rounded curves that are shown on a cumulative graph." },
    { term: "Limited hold (LH)", definition: "An interval-schedule addition that makes reinforcement available only for a finite time after the interval elapses.", sourceHint: "When a limited hold is added to an interval schedule, reinforcement remains available for a finite time following the elapse of the FI or VI interval." },
    { term: "Schedule thinning", definition: "Gradually decreasing the density of reinforcement (raising the ratio or lengthening the interval).", sourceHint: "Two procedures are used commonly for schedule thinning. First, the response ratio or the duration of the time interval is gradually increased." },
    { term: "Ratio strain", definition: "Disrupted responding that results from abrupt increases in ratio requirements during thinning.", sourceHint: "Ratio strain can result from abrupt increases in ratio requirements when moving from denser to thinner reinforcement schedules." },
    { term: "Differential reinforcement of high rates (DRH)", definition: "Reinforcement contingent on responses occurring at or above a criterion rate.", sourceHint: "DRH schedules produce higher rates of responding." },
    { term: "Differential reinforcement of low rates (DRL)", definition: "Reinforcement contingent on responses occurring at or below a criterion rate.", sourceHint: "DRL schedules produce lower rates of responding." },
    { term: "Differential reinforcement of diminishing rates (DRD)", definition: "Reinforcement at the end of a time interval when response count is less than a gradually decreasing criterion.", sourceHint: "The differential reinforcement of diminishing rates schedule provides reinforcement at the end of a predetermined time interval when the number of responses is less than a criterion that is gradually decreased." },
    { term: "Lag schedule", definition: "Reinforcement contingent on a response differing in some predetermined way (topography, sequence) from preceding responses.", sourceHint: "A lag schedule of reinforcement is one method to increase response variability." },
    { term: "Progressive ratio (PR) schedule", definition: "FR variant in which the ratio requirement increases incrementally within a session; used to test reinforcer strength.", sourceHint: "A progressive ratio schedule of reinforcement is a variation of the fixed ratio (FR) schedule in which the ratio requirements increase incrementally within the session." },
    { term: "Multiple schedule (mult)", definition: "Two or more basic schedules alternate, each correlated with a distinct SD.", sourceHint: "A multiple schedule (mult) presents two or more basic schedules of reinforcement in an alternating, usually random, sequence." },
    { term: "Mixed schedule (mix)", definition: "Like a multiple schedule, but no SDs signal which component is in effect.", sourceHint: "A mixed schedule of reinforcement (mix) uses a procedure identical to that of the multiple schedules, except no discriminative stimuli signal the presence of the independent component schedules." },
    { term: "Chained schedule (chain)", definition: "Two or more schedules occur in a fixed sequence; each link has its own SD.", sourceHint: "A chained schedule (chain) is similar to a multiple schedule. The multiple and chained schedules have two or more basic schedule requirements that occur successively, and have a discriminative stimulus correlated with each independent schedule." },
    { term: "Tandem schedule (tand)", definition: "Like a chained schedule, but no SDs signal each link.", sourceHint: "A tandem schedule (tand) operates like the chained schedule, except that discriminative stimuli are not used with the elements in the chain." },
    { term: "Concurrent schedule (conc)", definition: "Two or more contingencies operate independently and simultaneously for two or more behaviors; each has its own SD.", sourceHint: "A concurrent schedule (conc) of reinforcement occurs when (a) two or more contingencies of reinforcement (b) operate independently and simultaneously (c) for two or more behaviors." },
    { term: "Alternative schedule (alt)", definition: "Two or more schedules are simultaneously available; whichever requirement is met first delivers reinforcement.", sourceHint: "An alternative schedule (alt) provides an opportunity for reinforcement on two or more simultaneously available basic schedules of reinforcement." },
    { term: "Conjunctive schedule (conj)", definition: "Reinforcement requires the response requirements of two or more schedules to be met simultaneously.", sourceHint: "A conjunctive schedule (conj) of reinforcement is in effect whenever reinforcement follows the completion of response requirements for two or more schedules of reinforcement." },
    { term: "Behavioral contrast", definition: "In a multiple schedule, a change in one component's reinforcement rate produces an opposite-direction change in an unaltered component.", sourceHint: "Behavioral contrast describes the effects of (a) a schedule change that increases or decreases the rate of responding in one component of a multiple schedule of reinforcement, which is accompanied by (b) a change in the response rate in the opposite direction on another unaltered component." },
  ],
  procedures: [
    {
      title: "Move from acquisition to maintenance via schedule thinning",
      intro: "CRF builds new behavior; intermittent schedules maintain it.",
      steps: [
        { title: "Start with CRF", detail: "Reinforce every correct response during initial acquisition." },
        { title: "Switch to a dense intermittent schedule", detail: "FR-2 or FR-3 (or VR with average 2-3) once acquisition is reliable." },
        { title: "Thin gradually", detail: "Increase ratio or interval in SMALL increments; let behavior stabilize between increments." },
        { title: "Watch for ratio strain", detail: "If responding drops or becomes erratic, return to a denser schedule and thin again more slowly." },
        { title: "Communicate the rule", detail: "Use instructions, signs, or other prompts to make the schedule explicit when appropriate." },
      ],
    },
    {
      title: "Pick a schedule for your goal",
      intro: "Different schedules produce different patterns; pick to match the desired pattern.",
      steps: [
        { title: "Want HIGH steady rates?", detail: "VR or DRH." },
        { title: "Want a known number of responses per reinforcer?", detail: "FR (accept the postreinforcement pause)." },
        { title: "Want steady, low-moderate rates?", detail: "VI." },
        { title: "Want predictable timing (with scallop)?", detail: "FI - acknowledge the postreinforcement pause and accelerating end-of-interval rate." },
        { title: "Want LOW rates (e.g., reduce hand-raising frequency)?", detail: "DRL." },
        { title: "Want VARIABILITY?", detail: "Lag schedule." },
        { title: "Want to TEST reinforcer strength?", detail: "Progressive ratio - measure the break point." },
      ],
    },
  ],
  examples: [
    { scenario: "Factory worker is paid per 10 widgets produced.",
      application: "FR-10. Expect rapid 10-widget bursts with a postreinforcement pause after pay.",
      sourceHint: "FR schedules produce a typical pattern of responding: (a) After the first response of the ratio requirement, the participant completes the required responses with little hesitation between responses; and (b) a postreinforcement pause follows reinforcement." },
    { scenario: "A slot machine pays on average every 47 pulls.",
      application: "VR-47. Expect a high, steady rate with no postreinforcement pause - the source of slot machines' addictiveness.",
      sourceHint: "VR schedules produce consistent, steady rates of response. They typically do not produce a postreinforcement pause." },
    { scenario: "A student checks the mailbox starting just before the daily 1 PM delivery.",
      application: "FI-24h (approximately) with the typical FI scallop emerging as the interval nears.",
      sourceHint: "An initially slow, but accelerating, rate of response is evident toward the end of the interval, usually reaching a maximum rate just before delivery of the reinforcer." },
    { scenario: "A teacher praises a student for being on-task on average every 10 minutes (interval varies).",
      application: "VI-10. Expect a uniform, low-to-moderate steady rate of on-task behavior.",
      sourceHint: "A VI schedule of reinforcement tends to produce a low to moderate stable rate of response." },
    { scenario: "Speech-language pathologist reinforces only novel word combinations not used in the last three trials.",
      application: "Lag-3 schedule of reinforcement; designed to build response variability.",
      sourceHint: "A lag schedule of reinforcement is one method to increase response variability." },
  ],
  comparisons: [
    {
      title: "Fixed vs Variable ratio/interval",
      left:  { label: "Fixed (FR, FI)", points: [
        "Constant requirement.",
        "Predictable, often produces postreinforcement pause.",
        "FI shows the scallop.",
      ]},
      right: { label: "Variable (VR, VI)", points: [
        "Requirement varies around an average.",
        "Smoother, steadier responding.",
        "No reliable postreinforcement pause.",
      ]},
    },
    {
      title: "Multiple vs Mixed; Chained vs Tandem",
      left:  { label: "Multiple / Chained (with SDs)", points: [
        "Each component has a distinct SD.",
        "Discriminated; subject knows which schedule is active.",
      ]},
      right: { label: "Mixed / Tandem (no SDs)", points: [
        "Components alternate (mix) or chain (tand) without signals.",
        "Behavior is not under stimulus control of components.",
      ]},
    },
    {
      title: "Alternative vs Conjunctive",
      left:  { label: "Alternative (alt)", points: [
        "Whichever schedule's requirement is met FIRST delivers reinforcement.",
        "All requirements reset after reinforcement.",
      ]},
      right: { label: "Conjunctive (conj)", points: [
        "Requirements of TWO OR MORE schedules must be met simultaneously.",
        "Reinforcement only when ALL requirements are satisfied.",
      ]},
    },
  ],
  misconceptions: [
    {
      myth: "Intermittent reinforcement is less effective than continuous reinforcement.",
      reality: "CRF is for ACQUISITION; intermittent schedules are essential for MAINTENANCE. Intermittently reinforced behavior is generally more resistant to extinction.",
      sourceHint: "Continuous reinforcement (CRF) is used to strengthen behavior, primarily during the initial stages of learning new behaviors. Intermittent reinforcement (INT) is used to maintain an established behavior.",
    },
    {
      myth: "FI schedules produce steady rates throughout the interval.",
      reality: "FI produces a postreinforcement pause followed by an accelerating rate toward the end (the FI scallop).",
      sourceHint: "FI schedules typically produce a postreinforcement pause in responding during the early part of the interval. An initially slow, but accelerating, rate of response is evident toward the end of the interval.",
    },
    {
      myth: "You can thin a schedule as quickly as you want once behavior is solid.",
      reality: "Abrupt thinning produces ratio strain (drop or erratic responding). Use small, gradual increments.",
      sourceHint: "Ratio strain can result from abrupt increases in ratio requirements when moving from denser to thinner reinforcement schedules.",
    },
    {
      myth: "DRL schedules teach behavior to occur AT a target rate, not below it.",
      reality: "DRL reinforces responses occurring AT OR BELOW a criterion rate; it lowers rate. DRH reinforces above-criterion responding.",
      sourceHint: "DRH schedules produce higher rates of responding. DRL schedules produce lower rates of responding.",
    },
    {
      myth: "Multiple and mixed schedules are interchangeable.",
      reality: "Multiple schedules use SDs; mixed schedules do not. The presence of SDs determines whether stimulus control develops over each schedule.",
      sourceHint: "A mixed schedule of reinforcement (mix) uses a procedure identical to that of the multiple schedules, except no discriminative stimuli signal the presence of the independent component schedules.",
    },
  ],
  examChecklist: [
    "Define a schedule of reinforcement and describe CRF vs INT.",
    "Name the four basic intermittent schedules and the typical response pattern for each.",
    "Define postreinforcement pause and the FI scallop.",
    "Explain limited hold and its effect on responding.",
    "Define schedule thinning and ratio strain.",
    "Distinguish DRH, DRL, DRD, and lag schedules.",
    "Explain progressive ratio and break point.",
    "Define and contrast multiple, mixed, chained, and tandem schedules.",
    "Define concurrent, alternative, and conjunctive schedules.",
    "Define behavioral contrast with one example.",
  ],
  flashcardTerms: [
    "Schedule of reinforcement",
    "Continuous reinforcement (CRF)", "Intermittent reinforcement (INT)",
    "Fixed ratio (FR) schedule", "Variable ratio (VR) schedule",
    "Fixed interval (FI) schedule", "Variable interval (VI) schedule",
    "Postreinforcement pause", "FI scallop", "Limited hold (LH)",
    "Schedule thinning", "Ratio strain",
    "Differential reinforcement of high rates (DRH)",
    "Differential reinforcement of low rates (DRL)",
    "Differential reinforcement of diminishing rates (DRD)",
    "Lag schedule", "Progressive ratio (PR) schedule",
    "Multiple schedule (mult)", "Mixed schedule (mix)",
    "Chained schedule (chain)", "Tandem schedule (tand)",
    "Concurrent schedule (conc)", "Alternative schedule (alt)", "Conjunctive schedule (conj)",
    "Behavioral contrast",
  ],
  quiz: [
    {
      id: "Q1",
      question: "Which schedule typically produces a postreinforcement pause AND an accelerating rate of response toward the end of the interval?",
      options: [
        { label: "A", text: "Fixed ratio (FR)" },
        { label: "B", text: "Variable ratio (VR)" },
        { label: "C", text: "Fixed interval (FI)" },
        { label: "D", text: "Variable interval (VI)" },
      ],
      answer: "C",
      explanation: "FI produces both a postreinforcement pause and the characteristic FI scallop.",
      topic: "FI scallop",
      sourceHint: "FI schedules typically produce a postreinforcement pause in responding during the early part of the interval. An initially slow, but accelerating, rate of response is evident toward the end of the interval.",
    },
    {
      id: "Q2",
      question: "Which schedule produces the steadiest, highest rate of responding with no PRP?",
      options: [
        { label: "A", text: "FR" },
        { label: "B", text: "FI" },
        { label: "C", text: "VI" },
        { label: "D", text: "VR" },
      ],
      answer: "D",
      explanation: "VR produces consistent, steady rates with no postreinforcement pause - the schedule behind slot machines.",
      topic: "VR pattern",
      sourceHint: "VR schedules produce consistent, steady rates of response. They typically do not produce a postreinforcement pause.",
    },
    {
      id: "Q3",
      question: "Ratio strain occurs when:",
      options: [
        { label: "A", text: "Behavior reaches a stable rate" },
        { label: "B", text: "A schedule is thinned abruptly from dense to thin" },
        { label: "C", text: "A limited hold is added" },
        { label: "D", text: "Reinforcer magnitude is increased" },
      ],
      answer: "B",
      explanation: "Abrupt jumps from dense to thin schedules produce ratio strain (disrupted responding).",
      topic: "Thinning",
      sourceHint: "Ratio strain can result from abrupt increases in ratio requirements when moving from denser to thinner reinforcement schedules.",
    },
    {
      id: "Q4",
      question: "Which compound schedule has NO SDs across its components?",
      options: [
        { label: "A", text: "Multiple" },
        { label: "B", text: "Chained" },
        { label: "C", text: "Tandem" },
        { label: "D", text: "Concurrent" },
      ],
      answer: "C",
      explanation: "Tandem schedules are like chained schedules but without SDs signaling each link. Mixed schedules are the no-SD analogue of multiple schedules.",
      topic: "Compound schedules",
      sourceHint: "A tandem schedule (tand) operates like the chained schedule, except that discriminative stimuli are not used with the elements in the chain.",
    },
    {
      id: "Q5",
      question: "A teacher praises every fifth correct math problem. This is a:",
      options: [
        { label: "A", text: "VR-5 schedule" },
        { label: "B", text: "FR-5 schedule" },
        { label: "C", text: "FI-5 schedule" },
        { label: "D", text: "VI-5 schedule" },
      ],
      answer: "B",
      explanation: "A fixed number (5) of responses is required - FR-5.",
      topic: "Naming schedules",
      sourceHint: "A fixed ratio (FR) schedule requires the completion of a specified number of responses before a response produces reinforcement.",
    },
    {
      id: "Q6",
      question: "Behavioral contrast occurs in which type of schedule?",
      options: [
        { label: "A", text: "Concurrent" },
        { label: "B", text: "Conjunctive" },
        { label: "C", text: "Multiple" },
        { label: "D", text: "Tandem" },
      ],
      answer: "C",
      explanation: "Behavioral contrast is the opposite-direction change in an unaltered component of a multiple schedule when another component changes.",
      topic: "Behavioral contrast",
      sourceHint: "Behavioral contrast describes the effects of (a) a schedule change that increases or decreases the rate of responding in one component of a multiple schedule of reinforcement.",
    },
    {
      id: "Q7",
      question: "A lag schedule is used to:",
      options: [
        { label: "A", text: "Increase response variability" },
        { label: "B", text: "Decrease response rate" },
        { label: "C", text: "Establish stimulus control" },
        { label: "D", text: "Reinforce escape behavior" },
      ],
      answer: "A",
      explanation: "Lag schedules reinforce responses that differ in some predetermined way from preceding responses - they build variability.",
      topic: "Lag schedules",
      sourceHint: "A lag schedule of reinforcement is one method to increase response variability.",
    },
  ],
  reviewPrompts: [
    "Sketch typical cumulative records for FR, VR, FI, and VI.",
    "Design a thinning sequence from CRF to VR-20 for a target behavior.",
    "Distinguish multiple, mixed, chained, and tandem schedules with one example each.",
    "Explain how a progressive ratio schedule reveals reinforcer strength.",
    "Predict behavioral contrast in a two-component multiple schedule where one component goes to extinction.",
    "Identify three lag-schedule applications for building variability in your work.",
  ],
  recap: [
    "Schedules of reinforcement are rules that determine which response gets the consequence. CRF builds new behavior; intermittent schedules maintain it. Ratio schedules count responses; interval schedules count time-plus-a-response. Fixed schedules are constant; variable schedules vary around an average.",
    "FR yields high rates with a postreinforcement pause; VR yields high steady rates; FI yields a scallop; VI yields steady moderate rates. Schedule thinning must be gradual to avoid ratio strain. Limited holds, DRH/DRL/DRD, lag, and progressive-ratio schedules each shape different patterns.",
    "Compound schedules (multiple, mixed, chained, tandem, concurrent, alternative, conjunctive) combine basic schedules with or without SDs. Behavioral contrast warns that changes in one component can shift responding in another. Apply schedule effects from basic research with caution in applied settings.",
  ],
  sourceExcerpts: [
    {
      heading: "Definition and boundaries",
      text: "A schedule of reinforcement is a rule that establishes the probability that a specific occurrence of a behavior will produce reinforcement. Continuous reinforcement and extinction provide the boundaries for all other schedules of reinforcement. Only selected occurrences of behavior produce reinforcement with an intermittent schedule of reinforcement.",
    },
    {
      heading: "FI scallop",
      text: "An FI schedule typically produces a postreinforcement pause in responding during the early part of the interval. An initially slow, but accelerating, rate of response is evident toward the end of the interval, usually reaching a maximum rate just before delivery of the reinforcer. This gradually accelerating rate of response toward the end of the interval is called an FI scallop because of the rounded curves that are shown on a cumulative graph.",
    },
    {
      heading: "Compound schedules taxonomy",
      text: "A multiple schedule presents two or more basic schedules in an alternating, usually random, sequence with a discriminative stimulus correlated with each. A chained schedule has two or more basic schedule requirements that occur successively with an SD per link. A mixed schedule is identical to a multiple except no SDs signal the components. A tandem schedule is like a chained schedule but without SDs. A concurrent schedule operates two or more contingencies independently and simultaneously for two or more behaviors.",
    },
    {
      heading: "Behavioral contrast",
      text: "Behavioral contrast describes the effects of (a) a schedule change that increases or decreases the rate of responding in one component of a multiple schedule of reinforcement, which is accompanied by (b) a change in the response rate in the opposite direction on another unaltered component.",
    },
  ],
};

export default chapter13Module;
